/**
 * How hard each pass thinks, in one place — and since D313 the person's answer
 * rather than the app's.
 *
 * Until D48 the answer lived at ten request sites, as four `high`, three
 * `xhigh`, one `low` and two reads of the temper's own level, each with a
 * paragraph beside it explaining why. D48 gathered them into one table of named
 * profiles, D76 put a picker on the profiles, and D313 hands over the level
 * itself: each kind of work thinks at the level set for it under Settings →
 * Routing (`EffortWork`, `DEFAULT_EFFORT` and `levelFor` in `core/models.ts`),
 * and this module's whole job is to say which kind of work each pass is.
 *
 * Three things this module deliberately does NOT do.
 *
 *  - **It does not clamp.** `resolveEffort` in `core/models.ts`, called from
 *    `transport.ts` and nowhere else, is the one place a level is lowered to what
 *    a model accepts. A second clamp here would be a second answer to "what did
 *    this model actually run at", and the Routing row's promise that a level
 *    above a model's top runs at its top rests on there being one.
 *  - **It does not read the model.** No `ModelEntry` reaches this file. A level
 *    that said "medium on Opus, high on Sonnet" would be the clamp above, moved.
 *  - **It does not read the temper, the verb, or anything a model said.** The
 *    temper changes what a move is asked and never how hard it thinks: a Reaching
 *    move was lifted to `xhigh` over whatever the profile said, which made the
 *    level the app's (D313). A packet of two or three verbs is one request at one
 *    level, Reasoning's. And a retry ladder that escalated on a weak answer stays
 *    refused for D48's reason: nothing here can tell a shallow move from a short
 *    one.
 */

import { levelFor, type Effort, type EffortLevels, type EffortWork } from '../core/models'

/**
 * The kinds of pass this app pays for, one per `streamObjects`/`streamText` call
 * site.
 *
 * Named for what the pass is rather than for the function that makes it, because
 * two of these share a function and several share a route: `runLinkScan` makes
 * both a `gather` and a `linkage` pass, and six classes are the check route.
 * Fifteen classes over six kinds of work, deliberately: the class is what a call
 * site, a test and a pass draw (D311) name, and `PASS_WORK` is the one place that
 * says which kind of work — and so which level — each of them is.
 */
export type PassClass =
  /** A reasoning verb on a node — `runOp`, and the packet `runPortfolio` sends. */
  | 'derive'
  /** The source check. */
  | 'verify'
  /** The survey of the field — the search V runs on the seed. */
  | 'survey'
  /** One seat of a panel. */
  | 'panel'
  /** The breakthrough scan that looks for bridges between distant branches. */
  | 'scan'
  /** `runLinkScan`'s web gather, whose JSON is discarded for its searches. */
  | 'gather'
  /** The prior-art search on a conjecture, which assumes it has been said
   *  before and goes after where. */
  | 'priorArt'
  /** The field sweep on a node, which reports where its literature is
   *  unsettled. */
  | 'sweep'
  /** The contract a conjecture's computational kill test runs under: its
   *  programs, its data, its metric and its two declared ranges. */
  | 'executable'
  /** `runLinkScan`'s reasoning pass over what the gather harvested. */
  | 'linkage'
  /** Writing the synthesis a bridge proposed. */
  | 'synthesis'
  /** The cross-examination, by a model from another house. */
  | 'audit'
  /** Asking a question about a node or the graph. */
  | 'chat'
  /** Proposing the move list a programme run will walk, for a person to approve. */
  | 'plan'
  /** Sharpening a seed into a claim that could be wrong, before anything is spent on it. */
  | 'sharpen'

/**
 * Which kind of work each pass is, and so whose level it thinks at.
 *
 * Exhaustive over `PassClass`, so a sixteenth pass stops the build until
 * somebody decides whose level it follows. Each row is the route the pass's
 * MODEL already follows, so the Routing row a person reads for "which model" is
 * the row they read for "how hard": `runLinkScan`'s gather and its reasoning step
 * both resolve the check route, a realised synthesis resolves Reasoning's
 * (`realizeBridge`), and the survey, the prior-art search and the field sweep are
 * searches on the check route like the check itself. The cross-examination runs
 * on another company's model with no route to follow, so it is its own kind.
 */
export const PASS_WORK: Readonly<Record<PassClass, EffortWork>> = {
  derive: 'reason',
  executable: 'reason',
  synthesis: 'reason',
  /* One plan is one pass, and it decides where a dozen paid passes go, so it
     thinks at the level set for the reasoning it is planning. The person
     approves the list either way. */
  plan: 'reason',
  verify: 'check',
  survey: 'check',
  gather: 'check',
  linkage: 'check',
  priorArt: 'check',
  sweep: 'check',
  panel: 'panel',
  scan: 'scan',
  chat: 'chat',
  /* Three sharper sentences from the person's own words is a rewrite, not
     reasoning, and it is the ask channel's argument exactly: the subject is text
     already on screen, and a seed sharpened at forty seconds a go is one nobody
     sharpens. */
  sharpen: 'chat',
  audit: 'audit',
}

/** What a pass is about to ask for. */
export interface PassEffortAsk {
  pass: PassClass
  /**
   * The workspace's levels — `effortLevels` in the store. Absent, every kind of
   * work thinks at its default, which is what the evaluation harnesses' baselines
   * resolve through, so a changed default moves them with the product rather than
   * leaving them measuring a policy it no longer ships (D41, D48).
   */
  levels?: EffortLevels
}

/**
 * The effort one pass asks for. Pure, and the only thing this module exports that
 * a call site calls.
 */
export function resolvePassEffort(ask: PassEffortAsk): Effort {
  return levelFor(ask.levels, PASS_WORK[ask.pass])
}
